/**
 * ReactionBar — 5-emoji reaction row for forum topics and replies.
 *
 * Design language: light pills, active reaction gets colored fill,
 * total-count shown when > 0. One shared component drives both topic-level
 * and reply-level reactions via `targetType` prop.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';

const REACTIONS = [
  { id: 'like',      emoji: '👍', label: 'Like' },
  { id: 'love',      emoji: '❤️', label: 'Love' },
  { id: 'celebrate', emoji: '🎉', label: 'Celebrate' },
  { id: 'fire',      emoji: '🔥', label: 'Fire' },
  { id: 'curious',   emoji: '🤔', label: 'Curious' },
];

const ReactionBar = ({
  targetType,       // 'topic' | 'reply'
  targetId,
  initialCounts = {},
  initialMyReaction = null,
  compact = false,
  onChange,         // optional: called with the updated summary
}) => {
  const { API } = useContext(AuthContext);
  const [counts, setCounts] = useState(initialCounts || {});
  const [mine, setMine] = useState(initialMyReaction);
  const [pending, setPending] = useState(false);

  // Keep local state in sync when parent re-hydrates.
  useEffect(() => { setCounts(initialCounts || {}); }, [initialCounts]);
  useEffect(() => { setMine(initialMyReaction); }, [initialMyReaction]);

  const react = async (id) => {
    if (pending) return;
    setPending(true);
    // Optimistic update — feels snappy even on slow networks.
    const prev = mine;
    const nextCounts = { ...counts };
    if (prev) nextCounts[prev] = Math.max(0, (nextCounts[prev] || 0) - 1);
    if (prev !== id) {
      nextCounts[id] = (nextCounts[id] || 0) + 1;
      setMine(id);
    } else {
      setMine(null);
    }
    setCounts(nextCounts);
    try {
      const r = await axios.post(`${API}/forum/react`, {
        target_type: targetType, target_id: targetId, reaction: id,
      });
      setCounts(r.data.counts || {});
      setMine(r.data.my_reaction);
      onChange?.(r.data);
    } catch {
      // rollback on failure
      setCounts(counts);
      setMine(prev);
    } finally { setPending(false); }
  };

  const total = Object.values(counts).reduce((s, n) => s + (n || 0), 0);

  return (
    <div
      className={`inline-flex items-center gap-1 flex-wrap ${compact ? 'text-xs' : 'text-sm'}`}
      data-testid={`reaction-bar-${targetType}-${targetId}`}
    >
      {REACTIONS.map((r) => {
        const active = mine === r.id;
        const c = counts[r.id] || 0;
        return (
          <button
            key={r.id}
            type="button"
            onClick={() => react(r.id)}
            aria-label={r.label}
            aria-pressed={active}
            title={r.label}
            data-testid={`reaction-${targetType}-${targetId}-${r.id}`}
            className={`inline-flex items-center gap-1 rounded-full border transition-colors
              ${compact ? 'h-6 px-1.5' : 'h-7 px-2'}
              ${active
                ? 'bg-primary/15 border-primary text-primary font-bold'
                : 'bg-card border-border text-foreground/70 hover:border-primary/60 hover:text-foreground'}`}
          >
            <span aria-hidden="true">{r.emoji}</span>
            {c > 0 && <span className="text-[10px] font-bold">{c}</span>}
          </button>
        );
      })}
      {total > 0 && !compact && (
        <span className="ml-1 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
          {total}
        </span>
      )}
    </div>
  );
};

export default ReactionBar;
